import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useBusinesses } from '@/context/BusinessContext';

/**
 * Wraps protected app pages that require at least one business.
 *
 * - While businesses are loading, shows a thin loading state.
 * - On a load error, offers a retry.
 * - If the user has no businesses, redirects to the onboarding wizard.
 * - Otherwise renders the page.
 *
 * Assumes it sits inside ProtectedRoute, so the user is already signed in.
 */
export function RequireBusiness({ children }: { children: ReactNode }) {
  const { businesses, loading, error, refresh } = useBusinesses();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-sm text-slate-600 dark:text-slate-400">{error}</p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          Try again
        </button>
      </div>
    );
  }

  if (businesses.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
